import { store } from '../../store/store';
import { 
  startUpload, 
  updateChunkProgress, 
  completeUpload, 
  failUpload,
  pauseUpload,
  resumeUpload,
  retryUpload,
  removeUpload,
  UPLOAD_STATUS
} from '../../store/slices/fileUpload.slice';
import { uploadFileInChunks } from '../../utils/fileUploadUtils';
import toast from 'react-hot-toast';

/**
 * Upload Service
 * Manages the actual file upload process with Redux integration
 */
class UploadService {
  constructor() {
    this.activeUploads = new Map(); // fileId -> { abortController, promise }
    this.maxConcurrentUploads = 2;
    this.uploadEndpoint = '/service/upload-chunk';
    this.fileStore = new Map(); // Store file references temporarily
    this.queueProcessingTimeout = null; // Debounce queue processing
    this.operationLocks = new Set(); // Prevent multiple operations on same file
  }

  /**
   * Set upload configuration
   */
  configure(options = {}) {
    const { 
      maxConcurrentUploads = 2, 
      uploadEndpoint = '/service/upload-chunk' 
    } = options;
    
    this.maxConcurrentUploads = maxConcurrentUploads;
    this.uploadEndpoint = uploadEndpoint;
  }

  /**
   * Store file reference for upload
   */
  storeFile(fileId, file) {
    this.fileStore.set(fileId, file);
  }

  /**
   * Get stored file reference
   */
  getFile(fileId) {
    return this.fileStore.get(fileId);
  }

  /**
   * Remove stored file reference
   */
  removeFile(fileId) {
    this.fileStore.delete(fileId);
  }

  /**
   * Start processing upload queue (debounced)
   */
  processUploadQueue() {
    // Clear existing timeout to debounce multiple rapid calls
    if (this.queueProcessingTimeout) {
      clearTimeout(this.queueProcessingTimeout);
    }
    
    this.queueProcessingTimeout = setTimeout(() => {
      this._processUploadQueueImmediate();
    }, 50);
  }
  
  /**
   * Internal method to actually process the queue
   */
  _processUploadQueueImmediate() {
    const state = store.getState();
    const uploads = Object.values(state.fileUpload.uploads);
    
    // Find pending uploads (skip paused ones)
    const pendingUploads = uploads.filter(
      upload => upload.status === UPLOAD_STATUS.PENDING
    );

    // Start uploads up to the concurrent limit
    const availableSlots = this.maxConcurrentUploads - this.activeUploads.size;
    const uploadsToStart = pendingUploads.slice(0, availableSlots);

    if (uploadsToStart.length > 0) {
      uploadsToStart.forEach(upload => {
        this.startUpload(upload.file_id);
      });
    }
  }

  /**
   * Start a single file upload
   */
  async startUpload(fileId) {
    const state = store.getState();
    const upload = state.fileUpload.uploads[fileId];
    
    if (!upload) {
      console.error(`Upload not found: ${fileId}`);
      return;
    }

    // Get the stored file
    const file = this.getFile(fileId);
    if (!file) {
      console.error(`File not found for upload: ${fileId}`);
      store.dispatch(failUpload({ 
        fileId, 
        error: 'File reference not found' 
      }));
      return;
    }

    try {
      // Mark as uploading
      store.dispatch(startUpload({ fileId }));

      // Determine upload endpoint (per-file override or global default)
      const uploadEndpoint =
        upload.uploadEndpoint && typeof upload.uploadEndpoint === 'string'
          ? upload.uploadEndpoint
          : this.uploadEndpoint;

      // Create abort controller
      const abortController = new AbortController();
      
      // Start the upload (with resume support)
      const uploadPromise = uploadFileInChunks(file, fileId, {
        uploadUrl: uploadEndpoint,
        signal: abortController.signal,
        startChunkIndex: upload.currentChunkIndex || 0, // Resume from where it left off
        folderId: upload.folderId, // Pass folderId if available
        onChunkComplete: ({ fileId, chunkIndex, progress }) => {
          store.dispatch(updateChunkProgress({ fileId, chunkIndex }));
        },
        onError: ({ fileId, error, message }) => {
          console.error(`Upload error for ${fileId}:`, error);
        }
      });
      
      // Debug logging for folderId

      // Store the upload info
      this.activeUploads.set(fileId, {
        abortController,
        promise: uploadPromise,
        startTime: Date.now()
      });

      // Wait for upload to complete
      const result = await uploadPromise;

      // Mark as completed with path information
      store.dispatch(completeUpload({ 
        fileId,
        path: result.path || null,
        url: result.url || null
      }));
      
      // Show success notification
      toast.success(`${file.name} uploaded successfully!`);
      

    } catch (error) {
      console.error(`Upload failed for ${fileId}:`, error);
      
      // Check if upload was aborted (paused/cancelled)
      if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
        return; // Don't mark as failed if it was intentionally aborted
      }
      
      // Mark as failed for actual errors
      store.dispatch(failUpload({ 
        fileId, 
        error: error.message || 'Upload failed' 
      }));
      
      // Show error notification
      toast.error(`Failed to upload ${file.name}: ${error.message}`);

    } finally {
      // Clean up active upload tracking
      this.activeUploads.delete(fileId);
      
      // Only remove file reference if upload completed successfully
      // Keep file references for paused and failed uploads so they can be resumed/retried
      const state = store.getState();
      const upload = state.fileUpload.uploads[fileId];
      if (upload && upload.status === UPLOAD_STATUS.COMPLETED) {
        this.removeFile(fileId);
      }
      
      // Process next items in queue
      setTimeout(() => this.processUploadQueue(), 100);
    }
  }

  /**
   * Pause an upload
   */
  pauseUpload(fileId) {
    // Prevent multiple operations on same file
    if (this.operationLocks.has(fileId)) {
      return;
    }
    
    this.operationLocks.add(fileId);
    
    try {
      const state = store.getState();
      const upload = state.fileUpload.uploads[fileId];
      
      // Only pause if currently uploading
      if (!upload || upload.status !== UPLOAD_STATUS.UPLOADING) {
        return;
      }
      
      const uploadInfo = this.activeUploads.get(fileId);
      if (uploadInfo) {
        // Abort the current upload
        uploadInfo.abortController.abort();
        this.activeUploads.delete(fileId);
        
        // Mark as paused in Redux
        store.dispatch(pauseUpload({ fileId }));
        
      }
    } finally {
      this.operationLocks.delete(fileId);
    }
  }

  /**
   * Resume a paused upload
   */
  resumeUpload(fileId) {
    // Prevent multiple operations on same file
    if (this.operationLocks.has(fileId)) {
      return;
    }
    
    this.operationLocks.add(fileId);
    
    try {
      const state = store.getState();
      const upload = state.fileUpload.uploads[fileId];
      
      // Only resume if currently paused
      if (!upload || upload.status !== UPLOAD_STATUS.PAUSED) {
        return;
      }
      
      // Check if we still have the file reference
      const file = this.getFile(fileId);
      if (!file) {
        console.error(`Cannot resume upload ${fileId}: File reference lost`);
        store.dispatch(failUpload({ 
          fileId, 
          error: 'Cannot resume: File reference lost' 
        }));
        return;
      }
      
      // Mark as pending so it gets picked up by queue processor
      store.dispatch(resumeUpload({ fileId }));
      
      // Process queue to start the resumed upload
      setTimeout(() => this.processUploadQueue(), 100);
      
    } finally {
      this.operationLocks.delete(fileId);
    }
  }

  /**
   * Retry a failed upload
   */
  retryUpload(fileId) {
    // Prevent multiple operations on same file
    if (this.operationLocks.has(fileId)) {
      return;
    }
    
    this.operationLocks.add(fileId);
    
    try {
      const state = store.getState();
      const upload = state.fileUpload.uploads[fileId];
      
      // Only retry if currently failed
      if (!upload || upload.status !== UPLOAD_STATUS.FAILED) {
        return;
      }
      
      // Check if we still have the file reference
      const file = this.getFile(fileId);
      if (!file) {
        console.error(`Cannot retry upload ${fileId}: File reference lost`);
        store.dispatch(failUpload({ 
          fileId, 
          error: 'Cannot retry: File reference lost' 
        }));
        return;
      }
      
      // Mark as pending so it gets picked up by queue processor
      store.dispatch(retryUpload({ fileId }));
      
      // Process queue to start the retried upload
      setTimeout(() => this.processUploadQueue(), 100);
      
    } finally {
      this.operationLocks.delete(fileId);
    }
  }

  /**
   * Cancel an upload
   */
  cancelUpload(fileId) {
    // Prevent multiple operations on same file
    if (this.operationLocks.has(fileId)) {
      return;
    }
    
    this.operationLocks.add(fileId);
    
    try {
      const state = store.getState();
      const upload = state.fileUpload.uploads[fileId];
      
      if (!upload) {
        return;
      }
      
      // Abort if currently active
      const uploadInfo = this.activeUploads.get(fileId);
      if (uploadInfo) {
        uploadInfo.abortController.abort();
        this.activeUploads.delete(fileId);
      }
      
      // Remove file reference
      this.removeFile(fileId);
      
      // Remove from Redux store
      store.dispatch(removeUpload({ fileId }));
      
    } finally {
      this.operationLocks.delete(fileId);
    }
  }

  /**
   * Cancel all active uploads
   */
  cancelAllUploads() {
    this.activeUploads.forEach((uploadInfo, fileId) => {
      uploadInfo.abortController.abort();
    });
    this.activeUploads.clear();
    this.fileStore.clear();
  }

  /**
   * Get upload statistics
   */
  getStats() {
    const state = store.getState();
    const uploads = Object.values(state.fileUpload.uploads);
    
    return {
      total: uploads.length,
      pending: uploads.filter(u => u.status === UPLOAD_STATUS.PENDING).length,
      uploading: uploads.filter(u => u.status === UPLOAD_STATUS.UPLOADING).length,
      completed: uploads.filter(u => u.status === UPLOAD_STATUS.COMPLETED).length,
      failed: uploads.filter(u => u.status === UPLOAD_STATUS.FAILED).length,
      paused: uploads.filter(u => u.status === UPLOAD_STATUS.PAUSED).length,
      active: this.activeUploads.size
    };
  }

  /**
   * Get overall progress
   */
  getOverallProgress() {
    const state = store.getState();
    const uploads = Object.values(state.fileUpload.uploads);
    
    if (uploads.length === 0) return 0;
    
    const totalProgress = uploads.reduce((sum, upload) => sum + upload.progress, 0);
    return Math.round(totalProgress / uploads.length);
  }
}

// Create singleton instance
const uploadService = new UploadService();

// Export both the class and instance
export { UploadService };
export default uploadService;

/**
 * Hook for using upload service in components
 */
export const useUploadService = () => {
  return {
    service: uploadService,
    configure: uploadService.configure.bind(uploadService),
    processQueue: uploadService.processUploadQueue.bind(uploadService),
    pauseUpload: uploadService.pauseUpload.bind(uploadService),
    resumeUpload: uploadService.resumeUpload.bind(uploadService),
    retryUpload: uploadService.retryUpload.bind(uploadService),
    cancelUpload: uploadService.cancelUpload.bind(uploadService),
    cancelAllUploads: uploadService.cancelAllUploads.bind(uploadService),
    getStats: uploadService.getStats.bind(uploadService),
    getOverallProgress: uploadService.getOverallProgress.bind(uploadService)
  };
};
